// Выбор элементов библиотеки для генерации. Отдельный диалог, а не
// `AttachmentPickerDialog`: тот выбирает файлы с per-kind лимитами и отдаёт
// `PendingUpload`, а элемент — доменная сущность (имя + набор картинок), уходит
// на бэк идентификатором и разворачивается там в snapshot.

import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useElementLightbox, useWorkspaceElements } from "~/entities/element";
import { useActiveWorkspace } from "~/entities/workspace";
import { cn } from "~/shared/lib/utils";
import { Button } from "~/shared/ui/button";
import { Input } from "~/shared/ui/input";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/shared/ui/dialog";

import { selectPickerElements } from "./element-picker-filter";
import { toggleElementId } from "./element-selection";
import { ElementPickerDetails } from "./ElementPickerDetails";
import { ElementPickerGrid } from "./ElementPickerGrid";

export interface ElementPickerDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Уже выбранные элементы (ids) — диалог открывается с ними. */
    selectedIds: readonly string[];
    maxElements: number;
    onConfirm: (elementIds: string[]) => void;
}

export function ElementPickerDialog({
    open,
    onOpenChange,
    selectedIds,
    maxElements,
    onConfirm,
}: ElementPickerDialogProps) {
    const { t } = useTranslation(["chat", "elements"]);
    const { activeWorkspaceId } = useActiveWorkspace();
    const query = useWorkspaceElements(activeWorkspaceId ?? undefined, { enabled: open });
    const [draft, setDraft] = useState<string[]>([...selectedIds]);
    // Просмотр показываем панелью внутри этого же окна, а не вторым диалогом:
    // деталь несёт свой лайтбокс, а он поверх двух вложенных Radix-модалок
    // теряет клики (нижний слой считает их «снаружи» и закрывает пикер).
    const [viewedId, setViewedId] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const lightbox = useElementLightbox();

    // Диалог переиспользуется между открытиями — на каждое открытие
    // синхронизируем черновик с актуальным выбором промпта.
    useEffect(() => {
        if (open) {
            setDraft([...selectedIds]);
            setViewedId(null);
            setSearch("");
        }
    }, [open, selectedIds]);

    const elements = query.data?.items ?? [];
    const viewed = elements.find((element) => element.id === viewedId) ?? null;
    const visible = selectPickerElements(elements, search, draft);

    // Пока открыт просмотр, закрытие окна (крестик, Escape, клик мимо) означает
    // «назад к выбору», а не выход: иначе набранный выбор терялся бы, и пикер
    // приходилось бы открывать заново ради одного взгляда на карточку.
    const handleOpenChange = (next: boolean) => {
        if (!next && viewed) {
            setViewedId(null);
            return;
        }
        onOpenChange(next);
    };

    return (
        // Пока лайтбокс открыт, диалог невидим и не модален — иначе Radix ловит
        // клики лайтбокса как «снаружи» и закрывает окно.
        <Dialog open={open} onOpenChange={handleOpenChange} modal={!lightbox.isOpen}>
            <DialogContent
                className={cn("max-w-3xl", lightbox.isOpen && "pointer-events-none opacity-0")}
                onEscapeKeyDown={(event) => {
                    if (lightbox.isOpen) {
                        lightbox.close();
                        event.preventDefault();
                    }
                }}
            >
                <DialogHeader>
                    <DialogTitle>
                        {viewed ? t("elements:details.title") : t("chat:prompt.elements.pickerTitle")}
                    </DialogTitle>
                    {/* В просмотре пояснение остаётся только для скринридера: под
                        ним сразу идут имя и поля элемента, читать про них нечего. */}
                    <DialogDescription className={cn(viewed && "sr-only")}>
                        {viewed
                            ? t("elements:details.description")
                            : t("chat:prompt.elements.pickerHint", { count: maxElements })}
                    </DialogDescription>
                </DialogHeader>

                {viewed ? (
                    <ElementPickerDetails
                        t={t}
                        element={viewed}
                        lightbox={lightbox}
                        onBack={() => setViewedId(null)}
                    />
                ) : (
                    <>
                        <div className="relative">
                            <Search
                                className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
                                aria-hidden
                            />
                            <Input
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder={t("chat:prompt.elements.searchPlaceholder")}
                                aria-label={t("chat:prompt.elements.searchPlaceholder")}
                                className="pl-9"
                            />
                        </div>

                        <ElementPickerGrid
                            t={t}
                            elements={visible.items}
                            isLoading={query.isLoading}
                            draft={draft}
                            maxElements={maxElements}
                            emptyText={
                                elements.length === 0
                                    ? t("chat:prompt.elements.empty")
                                    : t("chat:prompt.elements.notFound")
                            }
                            onToggle={(elementId) =>
                                setDraft((prev) => toggleElementId(prev, elementId, maxElements))
                            }
                            onView={setViewedId}
                        />

                        {/* Честно говорим, что на экране не вся библиотека: под
                            пустым поиском это последние добавленные, под запросом —
                            все совпадения. */}
                        {!query.isLoading && elements.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                                {visible.isRecent
                                    ? t("chat:prompt.elements.showingRecent", {
                                          count: visible.items.length,
                                          total: visible.total,
                                      })
                                    : t("chat:prompt.elements.showingFound", {
                                          count: visible.items.length,
                                          total: visible.total,
                                      })}
                            </p>
                        )}

                        <DialogFooter>
                            <span className="mr-auto text-xs text-muted-foreground">
                                {t("chat:prompt.elements.selectedCount", {
                                    selected: draft.length,
                                    max: maxElements,
                                })}
                            </span>
                            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                                {t("chat:prompt.elements.cancel")}
                            </Button>
                            <Button
                                type="button"
                                onClick={() => {
                                    onConfirm(draft);
                                    onOpenChange(false);
                                }}
                            >
                                {t("chat:prompt.elements.confirm")}
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
